import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { CategoriesAlert, UnsubscribeAllKeyResponse } from './entities';
import { NewsAlertsRestful } from './restful';
import { IncomingAuthorResult, IncomingCategoryUpdateResult, ingresCategory } from './ingress';
import { Session, Sound } from '@benzinga/session';
import { CategoryId } from '@benzinga/advanced-news-manager';

interface NewsRequestErrorEvent {
  error: SafeError;
  errorType:
    | 'get_category_alerts_error'
    | 'set_category_alerts_error'
    | 'get_author_subscriptions_error'
    | 'create_author_subscription_error'
    | 'remove_author_subscription_error'
    | 'unsubscribe_all_key_error'
    | 'unsubscribe_all_realtime_error';
  type: 'error';
}

export type NewsAlertsRequestEvent = NewsRequestErrorEvent;

export class NewsAlertsRequest extends ListenableSubscribable<NewsAlertsRequestEvent> {
  private restful: NewsAlertsRestful;

  constructor(session: Session) {
    super();
    this.restful = new NewsAlertsRestful(session);
  }

  public getCategoriesAlerts = async (): SafePromise<CategoriesAlert[]> => {
    const categories = await ingresCategory(this.restful.getCategoriesAlerts());
    if (categories.err) {
      this.dispatch({ error: categories.err, errorType: 'get_category_alerts_error', type: 'error' });
    }
    return categories;
  };

  public setCategoriesAlerts = async (
    categoryId: CategoryId,
    sound?: Sound,
    realtimeEmail?: boolean,
  ): SafePromise<IncomingCategoryUpdateResult> => {
    const categoriesRes = await this.restful.setCategoriesAlerts(categoryId, sound, realtimeEmail);
    if (categoriesRes.err) {
      this.dispatch({ error: categoriesRes.err, errorType: 'set_category_alerts_error', type: 'error' });
    }
    return categoriesRes;
  };

  public getAuthorSubscriptions = async (): SafePromise<IncomingAuthorResult> => {
    const subscriptions = await this.restful.getAuthorSubscriptions();
    if (subscriptions.err) {
      this.dispatch({
        error: subscriptions.err,
        errorType: 'get_author_subscriptions_error',
        type: 'error',
      });
    }

    return subscriptions;
  };

  public createAuthorSubscription = async (authorId: number, authorName: string): SafePromise<IncomingAuthorResult> => {
    const subscription = await this.restful.createAuthorSubscription(authorId, authorName);
    if (subscription.err) {
      this.dispatch({
        error: subscription.err,
        errorType: 'create_author_subscription_error',
        type: 'error',
      });
    }

    return subscription;
  };

  public removeAuthorSubscription = async (alertId: number): SafePromise<IncomingAuthorResult> => {
    const subscription = await this.restful.removeAuthorSubscription(alertId);
    if (subscription.err) {
      this.dispatch({
        error: subscription.err,
        errorType: 'remove_author_subscription_error',
        type: 'error',
      });
    }

    return subscription;
  };

  public getUnsubscribeAllKey = async (): SafePromise<UnsubscribeAllKeyResponse> => {
    const res = await this.restful.getUnsubscribeAllKey();
    if (res.err) {
      this.dispatch({ error: res.err, errorType: 'unsubscribe_all_key_error', type: 'error' });
    }
    return res;
  };

  public unsubscribeAllRealtime = async (unsubAllKey: string): SafePromise<void> => {
    const res = await this.restful.unsubscribeAllRealtime(unsubAllKey);
    if (res.err) {
      this.dispatch({ error: res.err, errorType: 'unsubscribe_all_realtime_error', type: 'error' });
    }
    return res;
  };
}
